import { useEffect } from 'react';
import Button, { DangerButton, PrimaryButton } from '../../../../../Button/src/tsx/Button';
import { useBookingContext } from '../../contexts/BookingContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useICSExport } from '../../hooks/useICSExport';
import { formatFullDate } from '../../utils/dates';
import Heading from '../atoms/Heading';
import AppointmentInfo from '../molecules/AppointmentInfo';
import Host from '../molecules/Host';
import Container, { Wrapper } from '../organisms/Container';
import Navigation from '../organisms/Navigation';

interface ExistingAppointmentProps {
	getLabel: (key: string) => string;
}

export default function ExistingAppointment({ getLabel }: ExistingAppointmentProps) {
	const { appointment, locale, service, setScene, timezone } = useBookingContext();

	const { gtmEventDownloadICS } = useAnalytics(service.businessId, service.serviceId);
	const { handleICSDownload } = useICSExport({
		appointmentData: appointment.data,
		getLabel,
		gtmEventDownloadICS,
	});

	useEffect(() => {
		setScene('existing_appointment');
	}, []);

	if (!appointment.data) {
		return null;
	}

	const { startDatetime, endDatetime, hostName, hostImageUrl, manageBookingUrl } = appointment.data;
	const timeSlotString = formatFullDate(startDatetime, endDatetime, locale, timezone.current);

	const DownloadButtonType = manageBookingUrl ? Button : PrimaryButton;

	return (
		<Container id="existing-appointment">
			<Wrapper>
				<Heading title={getLabel('booking_exists_heading')} subtitle={getLabel('booking_exists_subline')} />
				<AppointmentInfo heading={getLabel('appointment_info_preferred')} timeSlotString={timeSlotString}>
					<Host appointmentStartDateTime={startDatetime} getLabel={getLabel} hostImageUrl={hostImageUrl} hostName={hostName} />
				</AppointmentInfo>
			</Wrapper>
			<Navigation keyProp="existing-appointment">
				{manageBookingUrl && (
					<PrimaryButton href={manageBookingUrl} testId="manage-appointment" blank={true}>
						{getLabel('booking_button_manage_appointment')}
					</PrimaryButton>
				)}

				<DownloadButtonType onClick={handleICSDownload} testId="download-appointment-ics" specifier="file">
					{getLabel('booking_button_download_appointment')}
				</DownloadButtonType>

				<DangerButton onClick={appointment.cancel} testId="cancel-appointment">
					{getLabel('booking_button_cancel_appointment')}
				</DangerButton>
			</Navigation>
		</Container>
	);
}
